import { Alert } from "@/components/Alert";
import { Button } from "@/components/Button";
import { usePostJitaLogout, useUser } from "@/requests/hooks/user";
import { getAppConfig } from "@/utils/appManager";
import { doesUserHaveScope } from "@/utils/doesUserHaveScope";
import {
  faCircleNotch,
  faExclamationTriangle,
} from "@fortawesome/pro-duotone-svg-icons";
import { faCaretRight } from "@fortawesome/pro-solid-svg-icons";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { tv } from "tailwind-variants";

const styles = tv({
  slots: {
    whiteBg:
      "fixed top-40 z-[49] flex w-full bg-white md:relative md:top-[unset]",
    base: "z-50 mx-auto -mb-[150px] mt-[120px] flex w-full flex-grow justify-center bg-orange/25 px-20 transition-all duration-500 sm:px-70 md:mb-[unset] md:mt-[unset]",
    alertBody: "max-w-lg flex-grow items-center justify-center bg-white",
    titleContent: "flex w-full items-center justify-between",
  },
  variants: {
    fixedNavbar: {
      true: {
        whiteBg: "md:fixed md:top-50",
      },
    },
  },
});

const SuperAdminBanner = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { adminPanelHost } = getAppConfig();
  const [offset, setOffset] = useState<number>(0);
  const { base, alertBody, whiteBg, titleContent } = styles({
    fixedNavbar: offset >= 108,
  });

  const { data: user } = useUser();
  const {
    mutate: jitaLogout,
    isSuccess: isJitaLogoutSuccess,
    isPending: isPendingLoggingOut,
  } = usePostJitaLogout();

  useEffect(() => {
    const onScroll = () => setOffset(window.scrollY);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  if (isJitaLogoutSuccess) {
    window.location.replace(adminPanelHost);
    queryClient.removeQueries();
  }

  const onClickLogout = () => jitaLogout();

  if (!user) return null;

  const renderTitleSlot = () => (
    <div className={titleContent()}>
      {t("components.superAdminBanner.title", {
        customerName: user.customerName,
      })}
      <Button
        as="button"
        variant="link"
        icon={isPendingLoggingOut ? faCircleNotch : faCaretRight}
        loading={isPendingLoggingOut}
        iconClassName="w-20 h-20"
        iconPosition="end"
        trackingId={{
          eventName: "superadmin.logout",
          additionalProperties: {
            customer_name: user.customerName,
          },
        }}
        onClick={onClickLogout}>
        {t("components.superAdminBanner.cta")}
      </Button>
    </div>
  );

  return (
    doesUserHaveScope("JITA", user?.scopes) && (
      <div className={whiteBg()}>
        <div className={base()}>
          <div className={alertBody()}>
            <Alert
              variant="warning"
              icon={faExclamationTriangle}
              titleSlot={renderTitleSlot()}
              rounded="unset"
            />
          </div>
        </div>
      </div>
    )
  );
};

export default SuperAdminBanner;
